import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@auth';
import { PermissionsEnum } from '../models/permissions';

/**
 * What a document list page is asked to do on arrival, carried in its query string: `?new=1` opens the
 * page's "new document" form, `?open=<id>` opens a document's details, `?edit=<id>` opens it in its form.
 * The Home page sends these (quick actions, Continue on a draft, a task's document chip).
 */
export interface DocumentIntent {
  create: boolean;
  open?: string;
  edit?: string;
}

export const DOCUMENT_INTENT_PARAMS = {
  create: 'new',
  open: 'open',
  edit: 'edit'
} as const;

export function documentIntentParams(intent: Partial<DocumentIntent>): Params {
  const params: Params = {};
  if (intent.create) {
    params[DOCUMENT_INTENT_PARAMS.create] = 1;
  }
  if (intent.open) {
    params[DOCUMENT_INTENT_PARAMS.open] = intent.open;
  }
  if (intent.edit) {
    params[DOCUMENT_INTENT_PARAMS.edit] = intent.edit;
  }
  return params;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentIntentService {

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private authService: AuthService
  ) { }

  /**
   * Reads the intent off the route and removes the params from the URL (replacing the history entry), so
   * a reload or a back navigation does not open the form or the document a second time.
   */
  consume(route: ActivatedRoute): DocumentIntent {
    const params = route.snapshot.queryParamMap;
    const create = params.get(DOCUMENT_INTENT_PARAMS.create);
    const open = params.get(DOCUMENT_INTENT_PARAMS.open);
    const edit = params.get(DOCUMENT_INTENT_PARAMS.edit);
    const intent: DocumentIntent = {
      create: !!create && create !== '0' && create !== 'false',
      open: open || undefined,
      edit: edit || undefined
    };
    if (create !== null || open !== null || edit !== null) {
      this.router.navigate([], {
        relativeTo: route,
        queryParams: { [DOCUMENT_INTENT_PARAMS.create]: null, [DOCUMENT_INTENT_PARAMS.open]: null, [DOCUMENT_INTENT_PARAMS.edit]: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
    return intent;
  }

  /**
   * Whether the user may act on an intent. A page gates `create` on its create permission and `edit` on its
   * update permission plus the document's state, the same way its own buttons do, and falls back to the details.
   */
  hasPermission(permission: PermissionsEnum): boolean {
    return this.authService.hasPermission(permission);
  }

  /** The document the intent named no longer exists (or cannot be read) */
  notifyUnavailable(): void {
    this.snackBar.open(
      this.translateService.instant('home.documentUnavailable'),
      '✖',
      { direction: this.translateService.instant('uiDirection'), duration: 5000 }
    );
  }
}
